import { Component, OnDestroy, OnInit, inject } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { combineLatest, Observable, Subject } from "rxjs";
import { distinctUntilChanged, map, shareReplay, switchMap, takeUntil, tap } from "rxjs/operators";

import { TasksModel } from "../tasks.model";
import { FilterModel } from "../../filter/filter.model";
import { taskIsFiltered } from "../../home/filters";
import { TaskRecordType } from "../../shared/enums";
import { ApplicationState } from "../../shared/state/types";
import { PlanningActivity, Task } from "../../shared/types";
import { LanguageService } from "../../shared/services/language.service";

@Component({
	selector: "ic-task-overview",
	templateUrl: "./task-overview.component.html",
	styleUrls: ["./task-overview.component.scss"],
	standalone: false,
})
export class TaskOverviewComponent implements OnInit, OnDestroy {
	private readonly tasksModel = inject(TasksModel);
	private readonly store = inject<Store<ApplicationState>>(Store);
	private readonly route = inject(ActivatedRoute);
	private readonly router = inject(Router);
	private readonly filterModel = inject(FilterModel);
	private readonly languageService = inject(LanguageService);

	private readonly destroy$ = new Subject<void>();

	loading$ = this.store.select((state) => state.ui.tasks.loading);
	// @ts-expect-error TS2564
	tasks$: Observable<{ completed: Task[]; uncompleted: Task[] } | null>;
	currentShift$ = this.store.select((state) => state.data.shift);
	// @ts-expect-error TS2564
	planningActivities$: Observable<PlanningActivity[]>;
	// @ts-expect-error TS2322
	residentId$: Observable<string> = this.getResidentId();
	protected filteredTaskCodes$ = this.filterModel.filters$.pipe(
		map((filters) => (filters?.caretree?.length ? filters.caretree.map((task) => task.code) : [])),
	);
	protected filteredRoleCodes$ = this.filterModel.filters$.pipe(
		map((filters) =>
			filters?.accessGroups?.length ? filters.accessGroups.map((accessGroup) => accessGroup.code) : [],
		),
	);

	getResidentId() {
		// @ts-expect-error TS2531
		return this.route.parent.parent.paramMap.pipe(
			map((paramMap) => paramMap.get("patient")),
			shareReplay({ bufferSize: 1, refCount: true }),
		);
	}

	goToDetail(task: Task) {
		// @ts-expect-error TS2531
		const { patient } = this.route.parent.parent.snapshot.params;
		this.router.navigate([patient, "tasks", task.id]);
	}

	createDynamicTask() {
		// @ts-expect-error TS2531
		const { patient } = this.route.parent.parent.snapshot.params;
		this.router.navigate([patient, "task"]);
	}

	ngOnInit() {
		this.planningActivities$ = this.residentId$.pipe(
			switchMap((id) => this.tasksModel.getPlanningActivities(id)),
			takeUntil(this.destroy$),
		);

		this.residentId$
			.pipe(
				distinctUntilChanged(),
				tap((patient) => this.tasksModel.loadTasks(patient)),
				takeUntil(this.destroy$),
			)
			.subscribe();

		this.tasks$ = combineLatest([
			this.residentId$,
			this.currentShift$,
			this.filteredTaskCodes$,
			this.filteredRoleCodes$,
		]).pipe(
			map(([patient, shift, filteredTaskCodes, filteredRoleCodes]) => ({
				patient,
				shift,
				filteredTaskCodes,
				filteredRoleCodes,
			})),
			switchMap(({ patient, shift, filteredTaskCodes, filteredRoleCodes }) => {
				return this.tasksModel.getTasks(patient).pipe(
					map((tasks) => {
						if (!tasks) {
							return null;
						}

						const result: { completed: Task[]; uncompleted: Task[] } = {
							completed: [],
							uncompleted: [],
						};

						for (const task of tasks || []) {
							const enhancedTask: Task = {
								...task,
								isFiltered:
									!!(filteredTaskCodes?.length || filteredRoleCodes?.length)
									&& taskIsFiltered(
										{
											code: task.code,
											recordType: task.recordType as TaskRecordType,
											accessGroupCodes: task.accessGroups?.map((accessGroup) => accessGroup.code) ?? [],
										},
										filteredTaskCodes,
										filteredRoleCodes,
									),
							};
							if (task.completed) {
								result.completed.push(enhancedTask);
							} else {
								result.uncompleted.push(enhancedTask);
							}
						}
						// shifts and permissions are sorted first, then by translated name
						result.uncompleted = result.uncompleted.sort((a, b) => {
							const aShift = a.shift === shift.id ? 1 : 0;
							const bShift = b.shift === shift.id ? 1 : 0;
							if (aShift !== bShift) return aShift - bShift;

							const aPerm = a.hasUserHandlePermission ? 0 : 1;
							const bPerm = b.hasUserHandlePermission ? 0 : 1;
							if (aPerm !== bPerm) return aPerm - bPerm;

							return this.getTaskDisplayName(a.name).localeCompare(this.getTaskDisplayName(b.name));
						});

						result.completed = result.completed.sort(
							(a, b) => new Date(b.date ?? 0).getTime() - new Date(a.date ?? 0).getTime(),
						);

						return result;
					}),
				);
			}),
			takeUntil(this.destroy$),
		);
	}

	ngOnDestroy() {
		this.destroy$.next();
	}

	private getTaskDisplayName(name: Task["name"]): string {
		if (!name) return "";
		if (typeof name === "string") return name;
		const lang = this.languageService.language();
		return name[lang] || name[Object.keys(name)[0]] || "";
	}
}
